import { Box, Button, TextField, Tooltip, useMediaQuery } from "@mui/material";
import { Backdrop, CircularProgress } from "@mui/material";
import { AutocompleteChangeReason, IconButton } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { User } from "../../proto/api_pb";
import { mapToTalkUser } from "../../utils/TalkjsUtils";
import MessageIcon from "@mui/icons-material/Message";
import { backdropStyles, SelectUserContainer, styles } from "./styles";
import { Props } from "./types";
import { useNavigate, useParams } from "react-router-dom";
import { TEXT_COLOR } from "../../theme";
import { enterpriseName, isEnterprise } from "../../theme";
import Loading from "../../pages/Loading";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { TalkJsContext, TalkJsContextInterface } from "../../contexts";
import CloseIcon from "@mui/icons-material/Close";
import { MobileDrawer } from "../MobileDrawer/MobileDrawer";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const Messages: React.FC<Props> = ({
  autoCompleteOptions,
  isChatLoading,
  setParticipants,
  createConversation,
  setInbox,
  session,
  isSmallScreen,
  autoCreateChat,
}) => {
  const [conversationId, setConversationId] = useState<string>();
  const [conversationSubject, setConversationSubject] = useState<string>();
  const [isMessageBoxMaximized, setIsMessageBoxMaximized] =
    useState<boolean>(false);
  const { id, title } = useParams();
  const chatBoxEl = useRef<HTMLDivElement>(null);
  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const navigate = useNavigate();
  const isMobileScreen = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    if (id && title) {
      setConversationId(id);
      setConversationSubject(title);
    } else if (
      talkJsContext.conversationId &&
      talkJsContext.conversationSubject
    ) {
      setConversationId(talkJsContext.conversationId);
      setConversationSubject(talkJsContext.conversationSubject);
    }
  }, [
    id,
    talkJsContext.conversationId,
    talkJsContext.conversationSubject,
    title,
  ]);

  const handleAutocompleteChange = (
    _event: SyntheticEvent<Element, Event>,
    value: User[],
    _reason: AutocompleteChangeReason
  ) => {
    setParticipants(value.map(mapToTalkUser));
  };

  useEffect(() => {
    document.title = isEnterprise ? `${enterpriseName}` : "OmMuse";
  }, []);

  useEffect(() => {
    if (session && session.getInboxes().length < 0) return;
    if (autoCreateChat && chatBoxEl.current) {
      createConversation(conversationId, conversationSubject);
    }
  }, [
    autoCreateChat,
    conversationId,
    conversationSubject,
    createConversation,
    session,
  ]);

  useEffect(() => {
    if (session) {
      const talkInbox = session.createInbox();
      setInbox(talkInbox);
      talkInbox.mount(chatBoxEl.current);
      if (conversationId && conversationSubject) {
        talkInbox.select(conversationId);
      }
    }
  }, [chatBoxEl, session, setInbox, conversationId, conversationSubject]);

  const classes = backdropStyles();

  return (
    <>
      {isMobileScreen && (
        <Box sx={{ marginBottom: "15px" }}>
          <MobileDrawer />
        </Box>
      )}
      {isMobileScreen && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => navigate(-1)} sx={styles.backButton}>
            <ArrowBackIcon sx={{ marginRight: "10px" }} /> Back
          </Button>
        </Box>
      )}
      <div
        style={
          isMessageBoxMaximized
            ? styles.maximizedBox
            : {
                ...styles.drawerMessageContainer,
                transition: "width 1s",
                height: "calc(100vh - 310px)",
                width: isMobileScreen ? "95%" : "auto",
                marginLeft: isMobileScreen ? 10 : -20,
                minHeight: isMobileScreen ? "87vh" : "auto",
                overflowY: "auto",
                scrollbarWidth: "none",
              }
        }
      >
        {talkJsContext.isChatOpen && !isMobileScreen && (
          <Box sx={styles.chatBoxContainer}>
            {!isMessageBoxMaximized && !isMobileScreen ? (
              <IconButton onClick={() => setIsMessageBoxMaximized(true)}>
                <FullscreenIcon />
              </IconButton>
            ) : (
              <IconButton onClick={() => setIsMessageBoxMaximized(false)}>
                <FullscreenExitIcon />
              </IconButton>
            )}
            <Tooltip title="Close">
              <IconButton onClick={() => talkJsContext.setIsChatOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box
          sx={{ marginTop: -2 }}
          style={
            isSmallScreen === true
              ? { display: "block", margin: "-20px" }
              : { display: "flex" }
          }
        >
          {!isSmallScreen && (
            <SelectUserContainer
              sx={{
                width: isMobileScreen ? "95%" : "290px",
                marginLeft: isMessageBoxMaximized ? "65px" : "10px",
              }}
            >
              <Autocomplete
                multiple
                options={autoCompleteOptions}
                getOptionLabel={(option) => option.getUsername()}
                renderInput={(params) => (
                  <TextField {...params} label="Select users" />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.getUserId()}>
                    {option.getUsername()}
                  </li>
                )}
                onChange={handleAutocompleteChange}
                sx={styles.autoComplete}
              />
              <IconButton
                sx={{ color: TEXT_COLOR }}
                aria-label="edit"
                onClick={() => createConversation()}
              >
                <MessageIcon />
              </IconButton>
            </SelectUserContainer>
          )}
        </Box>
        <div
          className={classes.parent}
          ref={chatBoxEl}
          style={
            !isMessageBoxMaximized
              ? {
                  width: "100%",
                  transition: "height 1s",
                  height: isMobileScreen
                    ? "calc(100vh - 220px)"
                    : "calc(100vh - 240px)",
                }
              : { height: "calc(100vh - 100px)", transition: "height 1s" }
          }
        >
          <Backdrop
            className={classes.backdrop}
            open={isChatLoading}
            invisible={false}
          >
            <CircularProgress color="primary" />
          </Backdrop>
        </div>
      </div>
    </>
  );
};
export default withAuthenticationRequired(Messages, {
  onRedirecting: () => <Loading />,
});
